/**
 * In-app help and usage reference for planner workflows and keyboard interactions.
 *
 * @module apps/web/src/pages/HelpPage
 */

import { useMemo, useState } from 'react';
import {
  BookOpenText,
  BrainCircuit,
  CalendarCheck,
  FolderKanban,
  LifeBuoy,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/** Static product-help topics displayed locally; these descriptions do not inspect connection or permission state. */
const guideSections = [
  {
    title: 'Personal Planning',
    icon: CalendarCheck,
    body:
      'Personal mode is the source of truth for energy, emotion, working hours, sleep, breaks, vacation, and actual activity sessions. Team assignments appear here only for the assigned person.',
  },
  {
    title: 'Team Workspaces',
    icon: UsersRound,
    body:
      'Team mode manages shared projects, bundles, members, assignment, intake rules, and calendar feeds. A team can assign work into a member calendar, but it cannot see unrelated personal activities.',
  },
  {
    title: 'Projects, Bundles, Activities',
    icon: FolderKanban,
    body:
      'A project is the broad outcome. A bundle is a work stream or class inside that outcome. An activity is the schedulable process. Every activity needs a bundle; a bundle can exist without a project.',
  },
  {
    title: 'Readiness Fields',
    icon: ShieldCheck,
    body:
      'Custom fields are optional unless explicitly marked required. Required fields block scheduling until filled. Optional fields still train inference and improve future duration, emotion, and energy predictions.',
  },
  {
    title: 'Specificity and Weight',
    icon: SlidersHorizontal,
    body:
      'Specificity says how narrow a field is to a type of work. Weight says how much the field should matter at that specificity level. This lets Fluxability compare broad signals like emotion with narrow signals like class, client, or activity type.',
  },
  {
    title: 'Training Data',
    icon: BrainCircuit,
    body:
      'Session logs, prediction deltas, custom fields, assignee, bundle, project, emotion, duration, focus, and energy values are kept exportable so external models can learn from prediction error over time.',
  },
  {
    title: 'Email and CRM Intake',
    icon: Search,
    body:
      'Email rules map senders, domains, and keywords to team projects or bundles. SaiMail, Mail, and Zoho connections are recorded at the team level so intake can become proposals before assignment.',
  },
  {
    title: 'Calendar Sharing',
    icon: BookOpenText,
    body:
      'ICS feeds are scoped per person and permission level: busy/free, team names, team details, or full details. Use busy/free for external availability and team details for trusted project calendars.',
  },
];

/**
 * In-app help and usage reference for planner workflows and keyboard interactions.
 */
export function HelpPage() {
  const [query, setQuery] = useState('');
  const [question, setQuestion] = useState('');
  const answer = useMemo(() => answerQuestion(question), [question]);
  const sections = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return guideSections;
    return guideSections.filter(
      section =>
        section.title.toLowerCase().includes(normalized) || section.body.toLowerCase().includes(normalized)
    );
  }, [query]);

  return (
    <div className="min-h-full px-3 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <section className="rounded-md border bg-card/86 p-5 shadow-sm backdrop-blur-xl">
          <Badge variant="outline" className="mb-3 rounded-md">
            Workspace guide
          </Badge>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Help</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Practical operating notes for personal planning, team workspaces, custom fields,
                specificity, model training, intake, and permissioned calendar sharing.
              </p>
            </div>
            <div className="relative w-full lg:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search help"
                className="pl-9"
              />
            </div>
          </div>
        </section>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="grid gap-4 md:grid-cols-2">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <Card key={section.title} className="rounded-md">
                  <CardHeader className="p-4">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md border bg-muted">
                        <Icon className="h-4 w-4" />
                      </span>
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 text-sm leading-6 text-muted-foreground">
                    {section.body}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <LifeBuoy className="h-4 w-4" />
                Guide Assistant
              </CardTitle>
              <CardDescription>Ask operational questions about the current Fluxability model.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              <Textarea
                value={question}
                onChange={event => setQuestion(event.target.value)}
                placeholder="Example: how should I share only busy/free time?"
                className="min-h-28"
              />
              <Button type="button" variant="outline" onClick={() => setQuestion('')}>
                Clear
              </Button>
              <div className="rounded-md border bg-muted/35 p-3 text-sm leading-6">
                {answer || 'Ask about teams, required fields, email intake, sync policy, or ICS feeds.'}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Match local help keywords to fixed guidance, with an empty response for a blank question and a general fallback. This assistant does not call a model or inspect live integration state. */
function answerQuestion(question: string) {
  const value = question.trim().toLowerCase();
  if (!value) return '';
  if (value.includes('busy') || value.includes('ics') || value.includes('calendar')) {
    return 'Use Team mode -> Calendar Feeds & Links. Generate busy/free for external availability, team names for project calendars, and full details only for trusted private subscriptions.';
  }
  if (value.includes('required') || value.includes('field') || value.includes('missing')) {
    return 'A custom field only blocks scheduling when it is marked required. Optional fields still stay on the activity and are useful for training predictions.';
  }
  if (value.includes('specificity') || value.includes('weight')) {
    return 'Specificity measures how narrow a field is to a class of activity. Weight measures how strongly that field should influence decisions at its specificity level.';
  }
  if (value.includes('training') || value.includes('model') || value.includes('error')) {
    return 'Analytics exports prediction and actual data, including duration errors, session outcomes, custom field values, assignees, bundles, projects, emotions, energy, and focus. Use the Analytics page to track model error by class and over time.';
  }
  if (value.includes('team') && value.includes('custom')) {
    return 'Team custom fields are created inside Team mode. Required team fields block team activity assignment; optional fields still travel with activities and train inference.';
  }
  if (value.includes('email') || value.includes('mail') || value.includes('zoho')) {
    return 'Create sender, domain, or keyword rules in Team mode. The rule stores the project or bundle default and whether matching messages should become proposals or direct activities.';
  }
  if (value.includes('sync') || value.includes('personal') || value.includes('private')) {
    return 'Private keeps team data local, one-way in allows availability import without exporting team details, and two-way is for trusted team-personal synchronization.';
  }
  if (value.includes('bundle') || value.includes('project')) {
    return 'Projects represent overarching outcomes. Bundles represent work streams and are required for every activity. A bundle may optionally belong to a project.';
  }
  return 'Use Teams for shared structure and assignment; use Personal for energy, emotion, exact scheduling, actual sessions, and private constraints.';
}
